// HtmlResolver.js — DOM-based rich text → React with placeholders & lists
import React from 'react';
import PropTypes from 'prop-types';
import parse, { domToReact } from 'html-react-parser';
import { FormatAsCurrencyForLocale } from '@PetSureAustralia/petsure-react-ui-library/distLib/helpers';
import { useGetTooltipsQuery } from '../../data/kontentAi';
import { TooltipTrigger } from '../../components/tooltipTrigger/tooltipTrigger';
import { TAXONOMY_CODENAME } from '../../constants/cmsCodeName';
import {
  isAllowedByTaxonomyDropdownField,
  lowerCaseKeys,
  isAllowedByTaxonomyChecklistField
} from '../../helpers/kontentAiHelper';

/* ------------------------------------------------------------------ */
/* 1. placeholder helpers                                             */
/* ------------------------------------------------------------------ */

// Given an object like { a: { b: { c: 1 } } } and a string path "a.b.c", returns 1
export const getNestedValue = (obj, path) => {
  const keys = path.toLowerCase().split('.');
  return keys.reduce((acc, key) => acc?.[key], obj);
};

// Replace placeholders in your HTML string with values from dynamicValue
export const replacePlaceholders = (text = '', dynamicValue = {}, regEx = '', formatNumbers = true) => {
  const normalizedValue = lowerCaseKeys(dynamicValue);
  return text.replace(regEx, (_, key) => {
    const value = getNestedValue(normalizedValue, key.trim());
    if (value == null) return '';
    if (formatNumbers && typeof value === 'number' && !Number.isNaN(value)) {
      return FormatAsCurrencyForLocale(value, { trimZeroDecimals: true });
    }
    return value;
  });
};

export const replacePlaceholdersMultiplePatterns = (text = '', dynamicValue = {}, regExs = [], formatNumbers = true) =>
  regExs.reduce((result, regEx) => replacePlaceholders(result, dynamicValue, regEx, formatNumbers), text);

/* ------------------------------------------------------------------ */
/* 2. recursive lookup of linkedItems                                 */
/* ------------------------------------------------------------------ */
// Find the linked item matching a codename, searching nested linkedItems too
const findLinkedItemRecursive = (codename, items = []) => {
  const direct = items.find((i) => i.system?.codename === codename);
  if (direct) return direct;
  return items.reduce((found, itm) => {
    if (found) return found;
    const nested = itm.elements?.section?.linkedItems || itm.elements?.body_content?.linkedItems || [];
    return findLinkedItemRecursive(codename, nested);
  }, null);
};

/* ------------------------------------------------------------------ */
/* 3. DOM post-processors                                             */
/* ------------------------------------------------------------------ */
// Convert <li><p>…</p></li> into <li><span>…</span></li>
const stripParagraphInsideLi = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html;
  container?.querySelectorAll('li > p').forEach((p) => {
    const span = document.createElement('span');
    span.innerHTML = p.innerHTML;
    p.replaceWith(span);
  });
  return container.innerHTML;
};

// Remove <p><br></p> or <p>&nbsp;</p> blocks
const stripEmptyParagraphs = (html) => {
  const container = document.createElement('div');
  container.innerHTML = html;

  container?.querySelectorAll('p').forEach((p) => {
    const inner = p.innerHTML.trim();
    if (inner === '<br>' || inner === '' || inner === '&nbsp;') {
      p.remove();
    }
  });

  return container.innerHTML;
};

/* ------------------------------------------------------------------ */
/* 4. recursive rich-text resolver                                     */
/* ------------------------------------------------------------------ */
export const resolveRichTextWithLinkedItems = ({
  html,
  linkedItems = [],
  dynamicValue = {},
  depth = 0,
  dropdownField = {},
  cmsTags = {}
}) => {
  if (!html) return '';

  const frag = document.createRange().createContextualFragment(html);

  frag.querySelectorAll('table').forEach((table) => {
    table.classList.add('responsive-table');
    table.querySelectorAll('ul').forEach((ul) => {
      ul.classList.add('table-ul');

      ul.querySelectorAll('li').forEach((li) => {
        li.classList.add('table-li');
      });
    });
  });

  // Convert the fragment back to HTML string
  const finalHtml = Array.from(frag.childNodes)
    .map((node) => node.outerHTML || node.textContent)
    .join('');

  // 4.1 Replace placeholders
  const displayTagPattern = /\{\{\s*([^}]+)\s*\}\}/g; // Replace "{{key}}" placeholders in your HTML
  const cmsTagPattern = /\[\[\s*([^\]]+)\s*\]\]/g; // Replace "[[key]]" placeholders in your HTML
  const withDisplayTagPlaceholders = replacePlaceholders(finalHtml, dynamicValue, displayTagPattern);
  const withCmsTagPlaceholders = replacePlaceholders(withDisplayTagPlaceholders, cmsTags, cmsTagPattern);

  // 4.2 Parse into a DOM container
  const container = document.createElement('div');
  container.innerHTML = withCmsTagPlaceholders;

  // 4.3 Strip <p> inside <li>
  container.innerHTML = stripParagraphInsideLi(container.innerHTML);

  // 4.3.1 Remove empty <p><br></p> or <p>&nbsp;</p> blocks
  container.innerHTML = stripEmptyParagraphs(container.innerHTML);

  // 4.4 Resolve embedded components (<object data-codename="...">)
  container
    .querySelectorAll('object[data-type="item"][data-rel="component"], object[data-type="item"][data-rel="link"]')
    .forEach((el) => {
      const codename = el.getAttribute('data-codename');
      const item = findLinkedItemRecursive(codename, linkedItems);
      if (!item?.elements?.section) {
        el.remove();
        return;
      }

      if (!isAllowedByTaxonomyDropdownField(item, dropdownField, TAXONOMY_CODENAME.POLICY_COVER)) {
        el.remove();
        return;
      }

      if (
        !isAllowedByTaxonomyChecklistField(
          item,
          dropdownField?.OptionalBenefitType,
          TAXONOMY_CODENAME.OPTIONAL_BENEFIT_TYPE
        )
      ) {
        el.remove();
        return;
      }

      // Recursively resolve this linked section's HTML
      const nestedHtml = item.elements.section.value;
      const nestedLinks = item.elements.section.linkedItems || [];
      const resolved = resolveRichTextWithLinkedItems({
        html: nestedHtml,
        linkedItems: nestedLinks,
        dynamicValue,
        depth: depth + 2,
        dropdownField
      });

      // Strip any <p> in <li> there too
      const frag = document.createElement('div');
      frag.innerHTML = stripParagraphInsideLi(resolved);

      // If it’s exactly one <ul>, swap the entire object for it
      const onlyChild = frag.childNodes.length === 1 ? frag.childNodes[0] : null;
      const hostUl = el.previousElementSibling?.tagName === 'UL' ? el.previousElementSibling : null;

      if (onlyChild?.tagName === 'UL') {
        el.replaceWith(onlyChild);
      } else {
        el.replaceWith(...frag.childNodes);
      }
    });
  // 4.5 Final strip of any <p> inside <li>
  return stripParagraphInsideLi(container.innerHTML);
};

/* ------------------------------------------------------------------ */
/* 5. RichTextRenderer: HTML string → React elements                   */
/* ------------------------------------------------------------------ */
export const RichTextRenderer = ({ html }) => {
  const { data: tooltips } = useGetTooltipsQuery();

  const options = {
    // eslint-disable-next-line consistent-return
    replace: (node) => {
      if (node.type === 'tag' && node.name === 'a' && node.attribs['data-item-id']) {
        const id = node.attribs['data-item-id'];
        const { href } = node.attribs;

        const tip = tooltips.find((t) => t.id === id);
        if (!tip) return null;

        const tmp = document.createElement('div');
        tmp.innerHTML = tip.text;
        const tipText = tmp.innerText || '';

        return (
          <TooltipTrigger
            mode="hover"
            id={id}
            underline
            trigger={<span className="rt-link"> {domToReact(node.children, options)}</span>}
            content={tipText}
          />
        );
      }
    }
  };

  return <>{parse(html, options)}</>;
};

RichTextRenderer.propTypes = {
  html: PropTypes.string.isRequired
};

/* ------------------------------------------------------------------ */
/* 6. map a Kontent field to a React element                          */
/* ------------------------------------------------------------------ */
export const resolveRenderField = ({ field, key, dynamicValue = {}, dropdownField = {}, cmsTags = {} }) => {
  if (!field) return null;
  // 6.1 Plain text → <h4>
  if (field.type === 'text') {
    return <h4 key={key}>{replacePlaceholders(field.value, dynamicValue)}</h4>;
  }

  // 6.2 Rich text → directly render the parsed HTML, no extra wrapper
  if (field.type === 'rich_text') {
    const html = resolveRichTextWithLinkedItems({
      html: field.value,
      linkedItems: field.linkedItems,
      dynamicValue,
      dropdownField,
      cmsTags
    });
    return (
      <RichTextRenderer
        html={html}
        key={key}
      />
    );
  }

  // 6.3 Modular content → wrap in a single <ul>
  if (field.type === 'modular_content') {
    const items = field.linkedItems || [];
    const listItems = items.map((itm, i) => {
      const inner = itm.elements.body_content?.value || itm.elements.section?.value;
      if (!inner) return null;
      const html = resolveRichTextWithLinkedItems({
        html: inner,
        linkedItems: itm.elements.body_content?.linkedItems || itm.elements.section?.linkedItems || [],
        dynamicValue,
        dropdownField,
        cmsTags
      });
      return (
        <li key={`${key}-${itm.system?.codename || i}`}>
          <RichTextRenderer html={html} />
        </li>
      );
    });
    return <ul key={key}>{listItems}</ul>;
  }

  return null;
};

resolveRenderField.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  field: PropTypes.object.isRequired,
  key: PropTypes.string,
  // eslint-disable-next-line react/forbid-prop-types
  dynamicValue: PropTypes.object
};
